import { ITypedReduxAction } from '../../types/redux/actionType';
import { Region } from '../../utils/regions';
import { changeLocation } from '../location';
import { IThunkActionCreator } from '../../types/redux';
import { filterToQuery } from '../../store/filters/query';
import { uniq, flatMap } from 'lodash';
import { defaultFilterState, Room } from '../../store/filters/model';
import { IRange } from '../../common/types';
import { setBanks } from '../banks';
import { mapGKData } from '../../store/pik_data/gks';
import { mapBannersData, mapBannersForRegion } from '../../store/pik_data/banners';
import { setBanners } from '../banners';

export enum EFilterActionTypes {
  RegionChange = 'FILTER/REGION_CHANGE',
  DateVariationsChange = 'FILTER/DATE_VARIATIONS_CHANGE',
  DateChange = 'FILTER/DATE_CHANGE',
  RoomsChange = 'FILTER/ROOMS_CHANGE',
  SquareChange = 'FILTER/SQUARE_CHANGE',
  PriceChange = 'FILTER/PRICE_CHANGE',
  DecorationChange = 'FILTER/DECORATION_CHANGE',
  HandOverChange = 'FILTER/HANDOVER_CHANGE',
  ResetFilter = 'FILTER/RESET',
}

export type TChangeDateVariations = ITypedReduxAction<EFilterActionTypes.DateVariationsChange, number[]>;
export type TChangeDate = ITypedReduxAction<EFilterActionTypes.DateChange, number>;
export type TRoomsChange = ITypedReduxAction<EFilterActionTypes.RoomsChange, Room[]>;
export type TRegionChange = ITypedReduxAction<EFilterActionTypes.RegionChange, Region>;
export type TSquareChange = ITypedReduxAction<EFilterActionTypes.SquareChange, IRange>;
export type TPriceChange = ITypedReduxAction<EFilterActionTypes.PriceChange, IRange>;
export type TDecorationChange = ITypedReduxAction<EFilterActionTypes.DecorationChange, boolean>;
export type THandOverChange = ITypedReduxAction<EFilterActionTypes.HandOverChange, boolean>;
export type TResetFilter = ITypedReduxAction<EFilterActionTypes.ResetFilter, Region>;

export const dateVariationsChange = (region: Region): IThunkActionCreator<void> => {
  return (dispatch, getState) => {
    const { pikData } = getState();
    const gks = (pikData && mapGKData(pikData)) || [];
    const filteredGksByRegion = gks && gks.filter(gk => gk.regions.includes(region));
    const dateVariations = uniq(flatMap(filteredGksByRegion, gk => gk.finishYears)).sort();

    dispatch({
      type: EFilterActionTypes.DateVariationsChange,
      payload: dateVariations,
    });
  };
};

export const regionChange = (region: Region): IThunkActionCreator<void> => {
  return (dispatch, getState) => {
    const {
      filter: { query },
      filter,
      tabs,
      pikData,
    } = getState();
    const banners = (pikData && mapBannersData(pikData)) || [];
    const filterQuery = filterToQuery({ ...query, region });
    const bannersForRegion = mapBannersForRegion([{ ...filter, query: { ...filter.query, region } }, banners]);

    dispatch(changeLocation(filterQuery, tabs));
    dispatch(dateVariationsChange(region));
    dispatch(setBanks({ ...query, region }));
    dispatch(setBanners(bannersForRegion));

    dispatch({
      type: EFilterActionTypes.RegionChange,
      payload: region,
    });
  };
};

export const resetFilterInRegion = (region: Region): IThunkActionCreator<void> => {
  return (dispatch, getState) => {
    const { tabs } = getState();
    const filterQuery = filterToQuery({ ...defaultFilterState.query, region });

    dispatch(changeLocation(filterQuery, tabs));
    dispatch(dateVariationsChange(region));
    dispatch(setBanks({ ...defaultFilterState.query, region }));

    dispatch({
      type: EFilterActionTypes.ResetFilter,
      payload: region,
    });
  };
};

export const dateChange = (date: number | undefined): IThunkActionCreator<void> => {
  return (dispatch, getState) => {
    const {
      filter: { dateVariations, query },
      tabs,
    } = getState();
    const newDate = date && dateVariations.includes(date) ? date : undefined;
    const filterQuery = filterToQuery({ ...query, date: newDate });

    dispatch(setBanks({ ...query, date: newDate }));
    dispatch(changeLocation(filterQuery, tabs));
    dispatch({
      type: EFilterActionTypes.DateChange,
      payload: newDate,
    });
  };
};

export const roomsChange = (rooms: Room[]): IThunkActionCreator<void> => {
  return (dispatch, getState) => {
    const {
      filter: { query },
      tabs,
    } = getState();
    const filterQuery = filterToQuery({ ...query, rooms });

    dispatch(setBanks({ ...query, rooms }));
    dispatch(changeLocation(filterQuery, tabs));
    dispatch({
      type: EFilterActionTypes.RoomsChange,
      payload: rooms,
    });
  };
};

export const squareChange = (square: IRange): IThunkActionCreator<void> => {
  return (dispatch, getState) => {
    const {
      filter: { query },
      tabs,
    } = getState();
    const filterQuery = filterToQuery({ ...query, square });

    dispatch(setBanks({ ...query, square }));
    dispatch(changeLocation(filterQuery, tabs));
    dispatch({
      type: EFilterActionTypes.SquareChange,
      payload: square,
    });
  };
};

export const priceChange = (price: IRange): IThunkActionCreator<void> => {
  return (dispatch, getState) => {
    const {
      filter: { query },
      tabs,
    } = getState();
    const filterQuery = filterToQuery({ ...query, price });

    dispatch(setBanks({ ...query, price }));
    dispatch(changeLocation(filterQuery, tabs));
    dispatch({
      type: EFilterActionTypes.PriceChange,
      payload: price,
    });
  };
};

export const decorationChange = (decoration: boolean): IThunkActionCreator<void> => {
  return (dispatch, getState) => {
    const {
      filter: { query },
      tabs,
    } = getState();
    const filterQuery = filterToQuery({ ...query, decoration });

    dispatch(setBanks({ ...query, decoration }));
    dispatch(changeLocation(filterQuery, tabs));
    dispatch({
      type: EFilterActionTypes.DecorationChange,
      payload: decoration,
    });
  };
};

export const handOverChange = (handOver: boolean): IThunkActionCreator<void> => {
  return (dispatch, getState) => {
    const {
      filter: { query },
      tabs,
    } = getState();
    const filterQuery = filterToQuery({ ...query, handOver });

    dispatch(setBanks({ ...query, handOver }));
    dispatch(changeLocation(filterQuery, tabs));
    dispatch({
      type: EFilterActionTypes.HandOverChange,
      payload: handOver,
    });
  };
};
